import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import BarcodeScanner from '../components/BarcodeScanner';
import { getPackTasks, getPackTask, packItem, completePackTask, getOrders, createPackTask, generateShippingLabel, shipOrder, packingSlipPdfUrl } from '../lib/api';
import HelpAnchor from '../components/HelpAnchor';
import { toast } from 'sonner';
import { enqueue as enqueueOffline } from '../lib/offlineQueue';
import { BoxArrowDown, Barcode, CheckCircle, Printer, Truck, Play, Plus, Camera, MagnifyingGlass } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';

export default function Packing() {
  const { user, isPacker } = useAuth();
  const [packTasks, setPackTasks] = useState([]);
  const [packSearch, setPackSearch] = useState('');
  const visiblePackTasks = useMemo(() => {
    const q = packSearch.trim().toLowerCase();
    if (!q) return packTasks;
    return packTasks.filter((pt) =>
      (pt.pack_task_number || '').toLowerCase().includes(q) ||
      (pt.order_number || '').toLowerCase().includes(q) ||
      (pt.customer_name || '').toLowerCase().includes(q)
    );
  }, [packTasks, packSearch]);
  const [loading, setLoading] = useState(true);
  const [activePackTask, setActivePackTask] = useState(null);
  const [scanInput, setScanInput] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [rfMode, setRfMode] = useState(localStorage.getItem('stockaris_rf_mode') === 'true');
  const toggleRfMode = () => {
    const next = !rfMode;
    setRfMode(next);
    localStorage.setItem('stockaris_rf_mode', String(next));
  };
  const [packedOrders, setPackedOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState('');
  const [carrier, setCarrier] = useState('sendle');

  const fetchData = async () => {
    try {
      const params = isPacker ? { packer_id: user.id } : {};
      const res = await getPackTasks(params);
      setPackTasks(res.data);
      const ordersRes = await getOrders({ status: 'picking' });
      setPackedOrders(ordersRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const startPacking = async (packTaskId) => {
    try {
      const res = await getPackTask(packTaskId);
      setActivePackTask(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleScan = async (barcodeOverride) => {
    const barcode = barcodeOverride || scanInput;
    if (!activePackTask || !barcode) return;
    const payload = { barcode, quantity: 1 };
    // Offline fast-path — queue and advance optimistically
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      enqueueOffline({
        path: `/api/pack-tasks/${activePackTask.id}/pack-item`,
        method: 'POST',
        body: payload,
        label: `Pack · ${String(activePackTask.id).slice(0, 8)}`,
      });
      toast.message('Offline — pack queued. Will sync when back online.');
      setScanInput('');
      setShowScanner(false);
      return;
    }
    try {
      await packItem(activePackTask.id, payload);
      setScanInput('');
      setShowScanner(false);
      const res = await getPackTask(activePackTask.id);
      setActivePackTask(res.data);
    } catch (e) {
      if (!navigator.onLine || e?.message === 'Network Error') {
        enqueueOffline({
          path: `/api/pack-tasks/${activePackTask.id}/pack-item`,
          method: 'POST',
          body: payload,
          label: `Pack · ${String(activePackTask.id).slice(0, 8)}`,
        });
        toast.message('Connection lost — pack queued. Will sync when back online.');
        setScanInput('');
      } else {
        console.error(e);
      }
    }
  };

  const handleComplete = async () => {
    if (!activePackTask) return;
    try {
      await completePackTask(activePackTask.id);
      setActivePackTask(null);
      fetchData();
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreateTask = async () => {
    if (!selectedOrder) return;
    try {
      await createPackTask({ order_id: selectedOrder });
      setShowCreate(false);
      setSelectedOrder('');
      fetchData();
    } catch (e) {
      console.error(e);
    }
  };

  const handleGenerateLabel = async (orderId) => {
    try {
      await generateShippingLabel({ order_id: orderId, carrier });
      fetchData();
    } catch (e) {
      console.error(e);
    }
  };

  const handleShip = async (orderId) => {
    try {
      await shipOrder(orderId);
      fetchData();
    } catch (e) {
      console.error(e);
    }
  };

  if (activePackTask) {
    const packedCount = activePackTask.packed_items?.length || 0;
    const totalItems = activePackTask.items?.reduce((sum, i) => sum + (i.quantity || 1), 0) || 0;
    const progress = totalItems > 0 ? (packedCount / totalItems) * 100 : 0;

    return (
      <Layout>
        <div data-testid="packing-active" className="p-4 md:p-6 max-w-2xl mx-auto">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h1 className="text-2xl font-bold flex items-center gap-2">
                {activePackTask.pack_task_number}
                <HelpAnchor articleId="packing-slip" label="How the customer packing slip works" />
              </h1>
              <div className="flex items-center gap-2">
                <a
                  href={packingSlipPdfUrl(activePackTask.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 border border-brand text-brand text-xs font-black uppercase tracking-widest hover:bg-brand hover:text-white transition-colors"
                  data-testid="packing-print-slip"
                  title="Print customer packing slip with serials"
                >
                  <Printer size={14} weight="fill" /> Packing slip
                </a>
                <Button variant="outline" onClick={() => setActivePackTask(null)}>Exit</Button>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Order: {activePackTask.order_number}</span>
              <span>•</span>
              <span>{packedCount}/{totalItems} items packed</span>
            </div>
            <Progress value={progress} className="h-2 mt-2" />
          </div>

          {/* Items List */}
          <div className="bg-card border border-border rounded-lg p-4 mb-6">
            <p className="overline mb-3">Items to Pack</p>
            <div className="space-y-2">
              {activePackTask.items?.map((item, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <span className="font-mono">{item.sku_code || 'SKU'}</span>
                  <span className="font-bold">x{item.quantity || 1}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Packed Items */}
          {activePackTask.packed_items?.length > 0 && (
            <div className="bg-green-50 border border-green-200 p-4 mb-6">
              <p className="overline mb-2 text-green-700">Packed Items</p>
              <div className="space-y-1">
                {activePackTask.packed_items.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-green-700">
                    <CheckCircle size={16} weight="fill" />
                    <span className="font-mono text-sm">{item.barcode}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Scan Input */}
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <label className="overline">Scan Mode</label>
              <button
                onClick={toggleRfMode}
                data-testid="pack-rf-mode-toggle"
                className={`px-3 py-1.5 text-xs font-bold uppercase border ${rfMode ? 'bg-brand text-white border-brand' : 'bg-card text-muted-foreground border-border'}`}
              >
                {rfMode ? 'RF MODE ON' : 'RF MODE OFF'}
              </button>
            </div>
            {!rfMode && (
              <Button
                onClick={() => setShowScanner(true)}
                data-testid="open-pack-scanner-btn"
                className="w-full h-16 text-lg bg-brand hover:bg-brand/90 font-bold"
              >
                <Camera size={24} className="mr-2" /> Scan with Camera
              </Button>
            )}
            <div className="relative">
              <Barcode size={24} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={scanInput}
                onChange={(e) => setScanInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleScan()}
                placeholder={rfMode ? "Trigger RF scanner..." : "Or enter barcode manually..."}
                className={`h-16 pl-14 text-lg font-mono ${rfMode ? 'border-2 border-brand bg-blue-50' : ''}`}
                data-testid="pack-scan-input"
                autoFocus={rfMode}
                autoComplete="off"
              />
            </div>
            <Button onClick={() => handleScan()} data-testid="pack-confirm-button" variant="outline" className="w-full h-16 text-lg font-bold">
              <CheckCircle size={24} className="mr-2" /> Scan Item
            </Button>
            {packedCount >= totalItems && (
              <Button onClick={handleComplete} className="w-full h-16 text-lg bg-green-600 hover:bg-green-700 font-bold">
                <Printer size={24} className="mr-2" /> Complete & Print Label
              </Button>
            )}
          </div>
        </div>
        {showScanner && (
          <BarcodeScanner
            onScan={(code) => handleScan(code)}
            onClose={() => setShowScanner(false)}
            title="Scan Item Barcode"
          />
        )}
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="packing-page" className="p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Packing</h1>
            <p className="text-muted-foreground">Pack orders and generate shipping labels</p>
          </div>
          {!isPacker && (
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
              <DialogTrigger asChild>
                <Button data-testid="create-pack-task-btn" className="bg-brand hover:bg-brand/90">
                  <Plus size={18} className="mr-2" /> New Pack Task
                </Button>
              </DialogTrigger>
              <DialogContent className="border border-border rounded-none">
                <DialogHeader>
                  <DialogTitle>Create Pack Task</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="overline">Select Order</label>
                    <Select value={selectedOrder} onValueChange={setSelectedOrder}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Select order" /></SelectTrigger>
                      <SelectContent>
                        {packedOrders.map(o => <SelectItem key={o.id} value={o.id}>{o.order_number} - {o.customer_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleCreateTask} className="w-full bg-brand hover:bg-brand/90">Create Task</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="grid gap-4">
          {/* Job-number search */}
          <div className="relative" data-testid="packing-search-bar">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="packing-search"
              placeholder="Search pack task / order number…"
              value={packSearch}
              onChange={(e) => setPackSearch(e.target.value)}
              className="pl-9 h-10 font-mono"
              autoComplete="off"
            />
          </div>
          {loading ? (
            [1,2,3].map(i => <div key={i} className="h-32 bg-slate-200 animate-pulse"></div>)
          ) : visiblePackTasks.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <BoxArrowDown size={48} className="mx-auto mb-4 text-slate-300" />
              <p className="text-muted-foreground">{packSearch ? `No pack tasks match "${packSearch}".` : 'No pack tasks available'}</p>
            </div>
          ) : visiblePackTasks.map(pt => {
            const packedCount = pt.packed_items?.length || 0;
            const totalItems = pt.items?.reduce((sum, i) => sum + (i.quantity || 1), 0) || 0;
            const progress = totalItems > 0 ? (packedCount / totalItems) * 100 : 0;
            
            return (
              <div key={pt.id} className="bg-card border border-border rounded-lg p-5">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <p className="font-mono font-bold text-lg">{pt.pack_task_number}</p>
                      <span className={`px-2 py-1 text-xs font-bold uppercase ${
                        pt.status === 'completed' ? 'bg-green-100 text-green-800' :
                        pt.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                        'bg-amber-100 text-amber-800'
                      }`}>
                        {pt.status}
                      </span>
                      {pt.auto_created && (
                        <span
                          data-testid={`auto-created-badge-${pt.id}`}
                          className="px-2 py-1 text-xs font-bold uppercase bg-brand/10 text-brand border border-brand/30"
                          title={pt.pick_list_number ? `From ${pt.pick_list_number}` : 'Auto-created from completed pick'}
                        >
                          From Pick
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>Order: {pt.order_number}</span>
                      {pt.customer_name && <span>{pt.customer_name}</span>}
                      <span>{totalItems} items</span>
                    </div>
                    <Progress value={progress} className="h-2 mt-3" />
                  </div>
                  <div className="flex gap-2">
                    {pt.status === 'completed' && (
                      <>
                        <Select value={carrier} onValueChange={setCarrier}>
                          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sendle">Sendle</SelectItem>
                            <SelectItem value="auspost">Australia Post</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="outline" onClick={() => handleGenerateLabel(pt.order_id)}>
                          <Printer size={18} className="mr-2" /> Label
                        </Button>
                        <Button onClick={() => handleShip(pt.order_id)} className="bg-green-600 hover:bg-green-700">
                          <Truck size={18} className="mr-2" /> Ship
                        </Button>
                      </>
                    )}
                    {pt.status !== 'completed' && (
                      <Button onClick={() => startPacking(pt.id)} data-testid="start-packing-btn" className="bg-brand hover:bg-brand/90">
                        <Play size={18} className="mr-2" /> Start Packing
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
